<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section Settings</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Section Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor Link" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild
            :use-background-mobile="true"
            :settings="fields.settings"
            :container-page="fields.containerPage"
          />
        </div>

        <div class="mb-4">
          <label class="label-input">Rating Summary</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="fields.ratingSummary.value"
                  type="number"
                  min="1"
                  max="5"
                  label="Stars (1-5)"
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker
                  :value="fields.ratingSummary.colorStar"
                  name="rating-summary-star-color"
                  label="Star Color"
                  default-color="#ccf46f"
                  @input="value => (fields.ratingSummary.colorStar = value)"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="fields.ratingSummary.text" label="Summary Text" hide-details />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex align-center justify-space-between mb-2">
            <label class="label-input mb-0">Title Testimonial</label>
            <v-checkbox v-model="fields.titleTestimonial.show" label="Show Title" hide-details class="mt-0" />
          </div>
          <ckeditor :editor="editor" v-model="fields.titleTestimonial.content" :config="editorConfig" />
        </div>

        <div class="mb-4">
          <label class="label-input">Tab Header Colors</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <ColorPicker
                  :value="fields.tabColors.colorTab"
                  name="tab-color"
                  label="Tab Text Color"
                  default-color="#23372b"
                  @input="value => (fields.tabColors.colorTab = value)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  :value="fields.tabColors.bgTabActive"
                  name="tab-bg-active"
                  label="Active Tab Background"
                  default-color="#23372b"
                  @input="value => (fields.tabColors.bgTabActive = value)"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Review Card Colors</label>
          <div class="group-item">
            <v-row>
              <v-col cols="12" md="4">
                <ColorPicker
                  :value="fields.cardColors.colorStar"
                  name="card-star-color"
                  label="Star Color"
                  default-color="#ccf46f"
                  @input="value => (fields.cardColors.colorStar = value)"
                />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker
                  :value="fields.cardColors.colorVerifiedBuyer"
                  name="card-verified-buyer-color"
                  label="Verified Buyer Color"
                  default-color="#365941"
                  @input="value => (fields.cardColors.colorVerifiedBuyer = value)"
                />
              </v-col>
              <v-col cols="12" md="4">
                <ColorPicker
                  :value="fields.cardColors.bgCardItem"
                  name="card-bg-item"
                  label="Card Background"
                  default-color="#F8F8F4"
                  @input="value => (fields.cardColors.bgCardItem = value)"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center">
            <label class="label-input mb-0">Tabs</label>
            <v-btn small text color="primary" @click="handleAddTab">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Tab</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(tab, tabIndex) in fields.tabs" :key="`tab-${tabIndex}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Tab {{ tabIndex + 1 }}: {{ tab.label || 'Untitled Tab' }}</span>
                    <v-btn icon :disabled="fields.tabs.length <= 1" @click.stop="handleRemoveTab(tabIndex)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="tab.label" label="Tab Label" class="mb-3" hide-details />

                  <div class="mt-4">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <span class="font-weight-bold">Review Cards</span>
                      <v-btn small text color="primary" @click="handleAddReview(tabIndex)">
                        <v-icon size="14">mdi-plus</v-icon> Add Review
                      </v-btn>
                    </div>

                    <v-expansion-panels accordion class="mb-2">
                      <v-expansion-panel
                        v-for="(review, reviewIndex) in tab.reviews"
                        :key="`review-${tabIndex}-${reviewIndex}`"
                      >
                        <v-expansion-panel-header>
                          <div class="d-flex justify-space-between align-center">
                            <span>Review {{ reviewIndex + 1 }}: {{ review.authorName || 'Untitled' }}</span>
                            <v-btn
                              icon
                              :disabled="tab.reviews.length <= 1"
                              @click.stop="handleRemoveReview(tabIndex, reviewIndex)"
                            >
                              <v-icon size="16" color="#ff6c6c">mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-text-field
                            v-model.number="review.rating"
                            type="number"
                            min="1"
                            max="5"
                            label="Rating (1-5)"
                            class="mb-3"
                            hide-details
                          />

                          <div class="mb-3">
                            <label class="d-block mb-1">Review Content (quote / heading / description)</label>
                            <ckeditor :editor="editor" v-model="review.bodyContent" :config="editorConfig" />
                          </div>

                          <div class="mb-3">
                            <label class="d-block mb-1">Avatar</label>
                            <UploadImage
                              @callback="obj => handleUploadAvatar(obj, tabIndex, reviewIndex)"
                              :image="{ imageSrc: review.avatar || '' }"
                              :height="`100`"
                              :typeUpload="'image'"
                              @callbackDelete="() => handleRemoveAvatar(tabIndex, reviewIndex)"
                            />
                          </div>

                          <v-text-field v-model="review.authorName" label="Author Name" class="mb-3" hide-details />
                          <v-text-field
                            v-model="review.authorMeta"
                            label="Author Meta (e.g. Farmer · Abilene, Kansas)"
                            class="mb-3"
                            hide-details
                          />
                          <v-text-field v-model="review.purchaseTag" label="Purchase Tag (e.g. 3 Pack)" hide-details />
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-3">
          <ButtonPrimary :button="fields.button" label="Button" />
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';
import ButtonPrimary from './common/button-primary.vue';
import ColorPicker from './common/color-picker.vue';

const defaultButton = {
  show: true,
  text: 'Built for Everyday Tasks',
  link: '#',
  backgroundColor: '#ff0000',
  color: '#ffffff',
  radius: 8,
  iconPrefix: {
    show: false,
    src: '',
  },
  iconSuffix: {
    show: true,
    src: 'https://cdn.skyfastimg.com/images/2026/06/11/image-6a2a87db55a093b7ad8c6903.png',
  },
  subBtn: {
    show: false,
    text: '',
  },
};

const defaultReviewBody = `
  <blockquote>"Mice got into my grain bins two years running. Lost hundreds of dollars of feed before I even noticed."</blockquote>
  <h3>"BINS HAVE BEEN CLEAN FOR FOUR MONTHS. NOT A SINGLE DROPPING."</h3>
  <p>Placed pouches around every bin entry and along the floor perimeter. Two weeks later the activity stopped completely. No poison near my feed, no dead mice to find. Just gone.</p>
`;

const makeReview = () => ({
  rating: 5,
  bodyContent: defaultReviewBody,
  avatar: '',
  authorName: 'Earl H.',
  authorMeta: '🌾 Farmer · Abilene, Kansas',
  purchaseTag: '3 Pack',
});

export default {
  name: 'sofyre-tabs-review-section-editor',
  components: {
    UploadImage,
    SectionSettingsContainerFuild,
    ButtonPrimary,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Sofyre Tabs Review Section',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: {
          enableStyle: false,
          background: '#FFFFFF',
        },
        ratingSummary: {
          value: 5,
          colorStar: '#ccf46f',
          text: '4.8 | 1668 Verified reviews',
        },
        titleTestimonial: {
          show: true,
          content: '<h2>Real Results From <br />People Like You</h2>',
        },
        tabColors: {
          colorTab: '#23372b',
          bgTabActive: '#23372b',
        },
        cardColors: {
          colorStar: '#ccf46f',
          colorVerifiedBuyer: '#365941',
          bgCardItem: '#F8F8F4',
        },
        tabs: [],
        button: { ...JSON.parse(JSON.stringify(defaultButton)) },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal && newVal.sectionExtract) {
          this.applyExtract(newVal.sectionExtract);
        }
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        ratingSummary: { ...this.fields.ratingSummary, ...(extract.ratingSummary || {}) },
        titleTestimonial: { ...this.fields.titleTestimonial, ...(extract.titleTestimonial || {}) },
        tabColors: { ...this.fields.tabColors, ...(extract.tabColors || {}) },
        cardColors: { ...this.fields.cardColors, ...(extract.cardColors || {}) },
        tabs:
          Array.isArray(extract.tabs) && extract.tabs.length
            ? JSON.parse(JSON.stringify(extract.tabs))
            : this.getDefaultTabs(),
        button: { ...JSON.parse(JSON.stringify(defaultButton)), ...(extract.button || {}) },
      };
    },
    getDefaultTabs() {
      return [
        { tabId: '1', label: '🏠 Homeowners', reviews: [makeReview()] },
        { tabId: '2', label: '🚐 RV Owner', reviews: [makeReview()] },
        { tabId: '3', label: '🌾 Farmers', reviews: [makeReview()] },
      ];
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionSofyreTabsReview(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddTab() {
      const newIndex = this.fields.tabs.length + 1;
      this.fields.tabs.push({
        tabId: String(newIndex),
        label: `New Tab ${newIndex}`,
        reviews: [makeReview()],
      });
    },
    handleRemoveTab(index) {
      if (this.fields.tabs.length <= 1) return;
      this.fields.tabs.splice(index, 1);
      this.fields.tabs.forEach((tab, i) => {
        tab.tabId = String(i + 1);
      });
    },
    handleAddReview(tabIndex) {
      if (!this.fields.tabs[tabIndex]) return;
      if (!this.fields.tabs[tabIndex].reviews) {
        this.$set(this.fields.tabs[tabIndex], 'reviews', []);
      }
      this.fields.tabs[tabIndex].reviews.push(makeReview());
    },
    handleRemoveReview(tabIndex, reviewIndex) {
      if (!this.fields.tabs[tabIndex] || this.fields.tabs[tabIndex].reviews.length <= 1) return;
      this.fields.tabs[tabIndex].reviews.splice(reviewIndex, 1);
    },
    handleUploadAvatar(obj, tabIndex, reviewIndex) {
      if (!this.fields.tabs[tabIndex] || !this.fields.tabs[tabIndex].reviews[reviewIndex]) return;
      this.$set(this.fields.tabs[tabIndex].reviews[reviewIndex], 'avatar', (obj && obj.imageSrc) || '');
    },
    handleRemoveAvatar(tabIndex, reviewIndex) {
      if (!this.fields.tabs[tabIndex] || !this.fields.tabs[tabIndex].reviews[reviewIndex]) return;
      this.$set(this.fields.tabs[tabIndex].reviews[reviewIndex], 'avatar', '');
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
